import React, { useEffect, useState } from "react";
import moment from "moment";
import {
  useFetchProviderCommunityRulesQuery,
  useGetProvidersMutation,
} from "../../../../../features/organisation/organisationApiSlice";
import {
  useFetchProviderPatientRulesMutation,
  useUpsertProviderPatientRulesMutation,
  useRemoveProviderPatientRulesMutation,
} from "../../../../../features/provider/providerApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../../features/auth/authSlice";
import {
  useCreatePatientConstraintMutation,
  useUpdatePatientConstraintMutation,
} from "../../../../../features/constraints/patient/patientConstraintApiSlice.js";
import ReusableModal from "../../../../../components/Modals/ReusableModal.jsx";
import { useCreateLogsMutation } from "../../../../../features/logs/logsApiSlice.js";
import {
  ACTIVITY_STATUS,
  ACTIVITY_TYPE,
} from "../../../../../constants/config.constants.js";
import Select from "react-select";
import { DAY_MAP } from "./CommunityVisitRulesTable.jsx";

const days = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const tabs = ["Global Settings", "Provider Settings"];

const PatientVisitRulesModal = ({
  handlePatientVisitRulesModal,
  details = null,
  setRefresh,
  visitRules,
  setVisitRules,
}) => {
  const [createLog] = useCreateLogsMutation();
  // const [patients, setPatients] = useState([]);

  const [patientId, setPatientId] = useState(
    details === null ? [] : details.patientId
  );
  const [selectedDays, setSelectedDays] = useState(
    details === null ? [] : details.visitDays
  );
  const [from, setFrom] = useState(
    details === null ? details : details.startDate
  );
  const [to, setTo] = useState(details === null ? details : details.endDate);
  const [isError, setIsError] = React.useState(false);

  const handleSelectAll = () => {
    setSelectedDays(days);
  };

  const verifyDetails = () => {
    if (patientId === null || selectedDays.length === 0) {
      return false;
    }

    return true;
  };

  // const [fetchPatientsAPI] = useGetPatientsMutation();

  const user = useSelector(selectUser);

  // const fetchPatients = async () => {
  //   try {
  //     const response = await fetchPatientsAPI(user.organisation.id).unwrap();

  //     const patients = response.data.map((patient) => ({
  //       name: patient.name,
  //       id: patient.id,
  //     }));

  //     setPatients(patients);
  //   } catch (error) {
  //     console.error(error?.data?.message ?? error.message);
  //   }
  // };

  const [createPatientConstraintAPI] = useCreatePatientConstraintMutation();

  const createPatientConstraint = async (data) => {
    try {
      const payload = {
        activity: `Visit rules created for the patient: ${details.name} by User - ${user.details?.name}`,
        activityType: ACTIVITY_TYPE.VISIT_RULES_HOME_UPDATE,
        data: {
          patientId: data.patient_id,
        },
      };
      try {
        await createPatientConstraintAPI({
          organisation_id: user.organisation.id,
          ...data,
        }).unwrap();

        setRefresh(true);
        setIsError(false);
        handlePatientVisitRulesModal();
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.SUCCESS,
        }).unwrap();
      } catch (error) {
        console.error(error?.data?.message ?? error.message);
        setIsError(true);
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.FAILED,
        }).unwrap();
      }
    } catch (error) {
      console.error(error?.data?.message ?? error.message);
    }
  };

  const [updatePatientConstraintAPI] = useUpdatePatientConstraintMutation();

  const updatePatientConstraint = async (data) => {
    try {
      const payload = {
        activity: `Visit rule updated for the patient: ${details.name} by User - ${user.details?.name}`,
        activityType: ACTIVITY_TYPE.VISIT_RULES_HOME_UPDATE,
        data: {
          patientId: data.patient_id,
        },
      };
      try {
        await updatePatientConstraintAPI(data).unwrap();

        const updatedRules = visitRules.map((rule) => {
          if (rule.id === data.id) {
            return {
              ...rule,
              patientId: data.patient_id,
              visitDays: data.visit_days,
              startDate: data.start_date,
              endDate: data.end_date,
            };
          }

          return rule;
        });

        setVisitRules(updatedRules);

        handlePatientVisitRulesModal();
        setIsError(false);
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.SUCCESS,
        }).unwrap();
      } catch (error) {
        console.error(error?.data?.message ?? error.message);
        await createLog({
          ...payload,
          status: ACTIVITY_STATUS.FAILED,
        }).unwrap();
      }
    } catch (error) {
      console.error(error?.data?.message ?? error.message);
    }
  };

  const [tab, setTab] = useState(0);

  const handleTabChange = (tabId) => {
    setTab(tabId);
  };

  const [getProviders, { isLoading: providerLoading }] =
    useGetProvidersMutation();

  const [getRules, { isLoading: getRulesLoading }] =
    useFetchProviderPatientRulesMutation();

  const [upsertRule, { isLoading: upsertRuleLoading }] =
    useUpsertProviderPatientRulesMutation();

  const [removeRule] = useRemoveProviderPatientRulesMutation();

  const [providers, setProviders] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);

  const getProvidersFunction = async () => {
    try {
      const { data } = await getProviders({
        orgId: user.organisation.id,
        limit: 0,
        status: ["ACTIVE"],
        showZones: false,
        showConstraints: false,
        showLeaves: false,
      }).unwrap();

      setProviders(
        data.map((provider) => ({ label: provider.name, value: provider.id }))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const getProviderRules = async () => {
    try {
      const { data } = await getRules(details.patientId).unwrap();

      setRules(data);
    } catch (err) {
      console.error(err);
    }
  };

  const addProviderRule = async () => {
    try {
      await upsertRule({
        providerId: selectedProvider.value,
        patientId: details.patientId,
        visitDays: selectedDays,
      }).unwrap();

      await getProviderRules();
    } catch (err) {
      console.error(err);
    }
  };

  const deleteProviderRule = async (providerId) => {
    try {
      await removeRule({
        patientId: details.patientId,
        providerId,
      }).unwrap();

      await getProviderRules();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    // fetchPatients();
    getProviderRules();
    getProvidersFunction();
  }, []);

  return (
    <ReusableModal
      open
      title={"Home Visit Rule"}
      onClose={handlePatientVisitRulesModal}
      boxClasses="xs:w-[300px] md:w-[800px] overflow-y-auto"
    >
      <div className="max-h-[76vh] overflow-auto">
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-col items-start justify-start w-full gap-2 p-4">
            <p className="m-0 font-bold">Patient</p>
            {details?.name && <p className="m-0">{details?.name}</p>}
            {/* :  */}
            {/* (
            <select
              className="block w-full p-2 text-sm text-gray-900 bg-white border border-gray-300 rounded md:w-fit focus:ring-blue-500 focus:border-blue-500"
              value={patientId}
              onChange={(e) => setPatientId(e.target.value)}
              disabled={details !== null}
            >
              <option value={null}>Select</option>
              {patients.map((patient, index) => (
                <option key={index} value={patient.id}>
                  {patient.name}
                </option>
              ))}
            </select>
          ) */}
            {/* } */}
          </div>
        </div>
        <ul id="tabs" class="inline-flex px-1 w-full border-b">
          {tabs.map((tabName, index) => (
            <li
              key={index}
              className={`px-4 py-2 text-gray-800 font-semibold rounded-t cursor-pointer ${
                tab === index
                  ? "text-primaryblue border-t border-r border-l -mb-px bg-white"
                  : ""
              }`}
              onClick={() => handleTabChange(index)}
            >
              <p className="m-0">{tabName}</p>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-start justify-between gap-2 p-2 px-4">
          <h1 className="m-0 text-lg font-bold">
            Availability
            <button className="ml-2 text-base" onClick={handleSelectAll}>
              (<span className="underline text-primaryblue">select all</span>)
            </button>
          </h1>
          <div className="flex flex-row flex-wrap w-full gap-3">
            {days.map((data, index) => (
              <label
                key={index}
                className="flex flex-row items-center gap-2 row-gap-4"
              >
                <input
                  type="checkbox"
                  className="w-4 h-4 checked:lightblue"
                  checked={selectedDays.includes(data)}
                  onChange={() => {
                    if (selectedDays.includes(data)) {
                      setSelectedDays(
                        selectedDays.filter((day) => day !== data)
                      );
                    } else {
                      setSelectedDays([...selectedDays, data]);
                    }
                  }}
                />
                <p className="m-0 text-sm">
                  {data[0] + data.substring(1).toLowerCase()}
                </p>
              </label>
            ))}
          </div>
        </div>
        {tab === 1 && (
          <div className="flex flex-col items-end gap-4 px-4 md:flex-row md:py-4">
            <div className="flex flex-col items-start justify-start w-full gap-2">
              <p className="m-0 font-bold">Provider</p>
              <Select
                isSearchable
                isClearable
                isLoading={providerLoading}
                isDisabled={providerLoading}
                options={providers}
                value={selectedProvider}
                onChange={(selected) => setSelectedProvider(selected)}
                className="w-full overflow-visible h-9 z-[1000]"
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                }}
              />
            </div>
            <button
              onClick={addProviderRule}
              disabled={upsertRuleLoading || providerLoading || getRulesLoading}
              className="px-6 h-9 font-semibold text-white border-[2px] rounded-md border-primaryblue bg-primaryblue whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {rules.some((rule) => rule.providerId === selectedProvider?.value)
                ? "Update"
                : "Add"}
            </button>
          </div>
        )}
        {tab === 0 && (
          <div className="flex flex-col items-center gap-4 px-4 md:flex-row md:py-4">
            <div className="flex flex-col w-full gap-2">
              <label className="font-semibold">From</label>
              <input
                type="date"
                className="w-full p-2 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                min={moment().format("YYYY-MM-DD")}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="flex flex-col w-full gap-2">
              <label className="font-semibold">To</label>
              <input
                type="date"
                className="w-full p-2 text-sm text-gray-900 bg-white border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                min={from}
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
          </div>
        )}
        {tab === 1 && (
          <div className="px-4 pb-4 overflow-scroll max-h-56">
            <table className="w-full">
              <thead>
                <tr className="text-left bg-primarylightgrey">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Availability</th>
                  <th className="px-4 py-3"></th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              {rules.length > 0 && (
                <tbody>
                  {rules.map((provider, index) => (
                    <tr
                      key={index}
                      className="border-b border-primarygrayborder"
                    >
                      <td className="px-4 py-3">{provider.providerName}</td>
                      <td className="px-4 py-3">
                        {provider.visitDays.map((x) => DAY_MAP[x]).join(", ")}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          disabled={
                            upsertRuleLoading ||
                            providerLoading ||
                            getRulesLoading
                          }
                          onClick={() => {
                            setSelectedProvider({
                              label: provider.providerName,
                              value: provider.providerId,
                            });
                          }}
                          className="font-bold text-primaryblue disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Edit
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          disabled={
                            upsertRuleLoading ||
                            providerLoading ||
                            getRulesLoading
                          }
                          onClick={() =>
                            deleteProviderRule(provider.providerId)
                          }
                          className="font-bold text-primaryred disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
            {rules.length === 0 && (
              <div className="w-full px-4 py-3 bg-slate-50">
                <p className="m-0 text-sm font-semibold text-center text-gray-500">
                  {getRulesLoading ? "Loading..." : "No Records Found"}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="w-full border border-primarygrayborder" />
      <div className="flex flex-col items-start justify-between gap-2 p-4">
        <div className="flex flex-row items-center justify-end w-full gap-4">
          <button
            className="px-4 py-2 font-bold text-white bg-gray-400 rounded-md"
            onClick={handlePatientVisitRulesModal}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 font-bold text-white rounded-md bg-primaryblue disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!verifyDetails()}
            onClick={async () => {
              if (details === null) {
                await createPatientConstraint({
                  patient_id: patientId,
                  visit_days: selectedDays,
                  start_date: from,
                  end_date: to,
                });
              } else {
                await updatePatientConstraint({
                  id: details.id,
                  patient_id: patientId,
                  visit_days: selectedDays,
                  start_date: from,
                  end_date: to,
                });
              }
            }}
          >
            Save
          </button>
        </div>
      </div>
    </ReusableModal>
  );
};

export default PatientVisitRulesModal;
